/**
 * 房间参与人与连接状态（教师上帝视角）。
 *
 * 展示房间码、连接状态、每位参与者的角色/阵营/辩位/在线状态。
 * Mock 后端没有逐人 presence 字段，因此在线状态按全局连接状态推断并在
 * 教师端标注口径。
 */
import { sideLabel } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState, cx } from '../ui/primitives.tsx';

const ROLE_LABEL = { TEACHER: '教师', DEBATER: '辩手' } as const;

function connectionTone(status: string): 'pass' | 'warning' | 'violation' | 'neutral' {
  if (status === 'connected') return 'pass';
  if (status === 'connecting' || status === 'reconnecting') return 'warning';
  if (status === 'error') return 'violation';
  return 'neutral';
}

export function ParticipantsPanel() {
  const { state, actions } = useArena();
  const debaters = state.participants.filter((participant) => participant.role === 'DEBATER');
  const online = state.connection === 'connected';

  return (
    <section className="rounded-xl border border-white/10 bg-bg-surface/80 backdrop-blur">
      <header className="flex items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">房间与参与人</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            房间码 <span className="mono tracking-[0.18em] text-team-aff">{state.roomId ?? '—'}</span>
          </p>
        </div>
        <div className="text-right" data-testid="coach-connection">
          <Badge tone={connectionTone(state.connection)}>
            {state.connection === 'connected'
              ? 'P2P 已连接'
              : state.connection === 'connecting'
                ? '连接中'
                : state.connection === 'reconnecting'
                  ? '重连中'
                  : state.connection === 'error'
                    ? '连接异常'
                    : '未连接'}
          </Badge>
          <div className="mt-1 text-[10px] text-slate-500">
            {state.peering.connectedPeers}/{state.peering.totalPeers || state.participants.length} peers
          </div>
        </div>
      </header>
      <div className="p-3">
        {state.participants.length === 0 ? (
          <EmptyState title="房间尚未加入参与人" detail="创建房间后，辩手加入会显示在这里。" />
        ) : (
          <ul className="space-y-1.5" data-testid="coach-participants">
            {state.participants.map((participant) => (
              <li
                key={participant.id}
                data-testid="participant-item"
                className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-xs font-medium text-slate-100">
                    {participant.name}
                    {state.self?.id === participant.id && (
                      <span className="ml-1 text-[10px] text-slate-500">（我）</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span>{ROLE_LABEL[participant.role]}</span>
                    {participant.side && (
                      <span className={participant.side === 'AFF' ? 'text-team-aff' : 'text-team-neg'}>
                        {sideLabel(participant.side)}
                      </span>
                    )}
                    {participant.seat && <span>{participant.seat} 辩</span>}
                  </div>
                </div>
                <span
                  className={cx(
                    'flex shrink-0 items-center gap-1 text-[10px]',
                    online ? 'text-assert-pass' : 'text-slate-500',
                  )}
                >
                  <span
                    className={cx(
                      'h-1.5 w-1.5 rounded-full',
                      online ? 'bg-assert-pass' : 'bg-slate-600',
                    )}
                  />
                  {online ? '在线' : '离线'}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
          <span>辩手 {debaters.length} 人 · 共 {state.participants.length} 人</span>
          <button
            type="button"
            className="text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
            onClick={() => actions.setRoute('review')}
          >
            前往复盘台 →
          </button>
        </div>
        <p className="mt-1 text-[10px] text-slate-600">
          在线状态口径：Phase 1 以房间连接状态推断全房间在线，逐人 presence 由 P2P 后端在重连恢复时补齐。
        </p>
      </div>
    </section>
  );
}
